import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Switch } from '@headlessui/react'
import clsx from 'clsx'

import { useBuildInfo } from 'hooks/use-build-info'
import { useFeatureFlags } from 'hooks/useFeatureFlag'
import { DEV_FEATURES } from 'virtual:dev-features'

export function SidebarDevBlock() {
	const { globalOverride, enableAll, disableAll } = useFeatureFlags()
	const apiBuild = useBuildInfo()
	const [featuresPos, setFeaturesPos] = useState<{ top: number; left: number } | null>(null)
	const [toolsPos, setToolsPos] = useState<{ top: number; left: number } | null>(null)
	const labelRef = useRef<HTMLSpanElement>(null)
	const toolsBtnRef = useRef<HTMLButtonElement>(null)
	const featuresTimer = useRef<number | null>(null)
	const toolsTimer = useRef<number | null>(null)

	const CLOSE_DELAY_MS = 1000

	const cancelClose = (timer: React.MutableRefObject<number | null>) => {
		if (timer.current !== null) {
			window.clearTimeout(timer.current)
			timer.current = null
		}
	}

	const scheduleClose = (
		timer: React.MutableRefObject<number | null>,
		setter: (v: null) => void
	) => {
		cancelClose(timer)
		timer.current = window.setTimeout(() => {
			setter(null)
			timer.current = null
		}, CLOSE_DELAY_MS)
	}

	useEffect(
		() => () => {
			cancelClose(featuresTimer)
			cancelClose(toolsTimer)
		},
		[]
	)

	const makeClamp = useCallback(
		(pos: { top: number; left: number } | null) => (el: HTMLDivElement | null) => {
			if (!el || !pos) return
			const rect = el.getBoundingClientRect()
			const padding = 8
			let top = pos.top

			if (rect.height + padding * 2 > window.innerHeight) {
				top = padding
			} else if (top + rect.height > window.innerHeight - padding) {
				top = window.innerHeight - padding - rect.height
			}
			if (top < padding) {
				top = padding
			}

			el.style.top = `${top}px`
		},
		[]
	)

	// Explicit allowlist — only show on local/dev/qa. Any unrecognised mode
	// (including stage, prod, production) is treated as production-safe and hides the block.
	if (!['local', 'development', 'dev', 'qa'].includes(import.meta.env.MODE)) {
		return null
	}

	const openFeatures = () => {
		if (!labelRef.current) return
		const rect = labelRef.current.getBoundingClientRect()
		setFeaturesPos({ top: rect.top, left: rect.right + 8 })
	}

	const openTools = () => {
		if (!toolsBtnRef.current) return
		const rect = toolsBtnRef.current.getBoundingClientRect()
		setToolsPos({ top: rect.top, left: rect.right + 8 })
	}

	return (
		<div className="mt-auto ml-1 pb-4 text-gray-600 text-[10px]/3 flex flex-col">
			<div className="flex flex-row gap-2 items-center mb-2">
				<Switch
					onChange={() => (globalOverride ? disableAll() : enableAll())}
					className={clsx(
						globalOverride ? 'bg-primary' : 'bg-white/10',
						'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
					)}
				>
					<span
						aria-hidden="true"
						className={clsx(
							globalOverride ? 'translate-x-4' : 'translate-x-0',
							'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
						)}
					/>
				</Switch>
				<span
					ref={labelRef}
					className="text-[11px] leading-5 cursor-default underline decoration-dotted decoration-gray-500"
					onMouseEnter={() => {
						cancelClose(featuresTimer)
						openFeatures()
					}}
					onMouseLeave={() => scheduleClose(featuresTimer, setFeaturesPos)}
				>
					Dev Mode
				</span>
				<span aria-hidden="true" className="text-gray-600 select-none">
					|
				</span>
				<button
					ref={toolsBtnRef}
					type="button"
					aria-label="Dev tools"
					className="inline-flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
					onMouseEnter={() => {
						cancelClose(toolsTimer)
						openTools()
					}}
					onMouseLeave={() => scheduleClose(toolsTimer, setToolsPos)}
					onFocus={openTools}
					onBlur={() => scheduleClose(toolsTimer, setToolsPos)}
				>
					<span aria-hidden="true" className="text-sm leading-none">
						🛠
					</span>
				</button>
			</div>
			<div>
				UI build: {__BUILD__.branchName} (
				{__BUILD__.repoUrl && __BUILD__.commitHash ? (
					<a
						href={`${__BUILD__.repoUrl}/tree/${__BUILD__.commitHash}`}
						target="_blank"
						rel="noreferrer"
						className="underline"
					>
						{__BUILD__.commitHash}
					</a>
				) : (
					__BUILD__.commitHash
				)}
				)
			</div>
			<div>{new Date(__BUILD__.commitDate ?? '').toLocaleString()}</div>
			{apiBuild && (
				<>
					<div className="mt-0.5">
						API build: {apiBuild.refName} (
						{apiBuild.commitUrl ? (
							<a href={apiBuild.commitUrl} target="_blank" rel="noreferrer" className="underline">
								{apiBuild.commit}
							</a>
						) : (
							apiBuild.commit
						)}
						)
					</div>
					<div>{new Date(apiBuild.buildDate).toLocaleString()}</div>
				</>
			)}
			{featuresPos &&
				createPortal(
					<div
						ref={makeClamp(featuresPos)}
						className="fixed z-[2147483647] w-64 max-h-[70vh] overflow-y-auto rounded-lg bg-primary-surface/[0.4] backdrop-blur-lg border border-translucent-plus-border p-3 shadow-xl"
						style={{ top: featuresPos.top, left: featuresPos.left }}
						onMouseEnter={() => cancelClose(featuresTimer)}
						onMouseLeave={() => scheduleClose(featuresTimer, setFeaturesPos)}
					>
						<p className="text-[10px] font-semibold text-white mb-2">Gated by Dev Mode</p>
						<div className="space-y-2.5">
							{DEV_FEATURES.map(({ group, features }) => (
								<div key={group}>
									<p className="text-[9px] font-semibold text-primary uppercase tracking-wider mb-1">
										{group}
									</p>
									<ul className="space-y-1">
										{features.map(feature => (
											<li
												key={feature}
												className="flex items-start gap-1.5 text-[10px] text-gray-300 leading-tight pl-1"
											>
												<span className="text-gray-500 shrink-0 mt-px">•</span>
												{feature}
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
					</div>,
					document.body
				)}
			{toolsPos &&
				createPortal(
					<div
						ref={makeClamp(toolsPos)}
						className="fixed z-[2147483647] w-56 max-h-[70vh] overflow-y-auto rounded-lg bg-primary-surface/[0.4] backdrop-blur-lg border border-translucent-plus-border p-3 shadow-xl"
						style={{ top: toolsPos.top, left: toolsPos.left }}
						onMouseEnter={() => cancelClose(toolsTimer)}
						onMouseLeave={() => scheduleClose(toolsTimer, setToolsPos)}
					>
						<p className="text-[10px] font-semibold text-white mb-2">Dev Tools</p>
						<div className="flex flex-col gap-1">
							<a
								href="/email-preview/"
								target="_blank"
								rel="noreferrer"
								className="group flex items-center justify-between rounded-md bg-primary/15 hover:bg-primary/30 border border-primary/30 px-2 py-1.5 text-[10px] font-medium text-white transition-colors"
							>
								<span>Email template previewer</span>
								<span
									className="text-primary group-hover:translate-x-0.5 transition-transform"
									aria-hidden="true"
								>
									→
								</span>
							</a>
						</div>
					</div>,
					document.body
				)}
		</div>
	)
}
